import { FC } from 'react';

import { NotificationsIcon } from '@componentLibrary/Icon';
import IconButton from '@componentLibrary/IconButton';

import Badge from '@components/layout/Badge';
import { useLocation } from '@components/router';

import { useApp } from '@hooks/AppHooks';

import { useEmployeeNotificationsUnreadCountQuery } from '../../queries/EmployeeNotificationsQueries';

const NOTIFICATIONS_PATH = '/user/notifications';

// Bell is only surfaced from the employee home (Sparks/Earn/Events tabs) so
// notification access stays anchored to the main screen. Spark/event detail
// views, profile, wallet, etc. should not show it.
const HOME_PATH_PATTERN = /^\/user\/sparks(\/(complete|upcoming))?\/?$/;

/**
 * Top-header bell that navigates employees to /user/notifications.
 *
 * Unlike the admin `NotificationWidget` (which opens an inline dropdown over the
 * legacy admin `Notification` collection), this bell is a simple link to a full
 * page so we can host filter chips, infinite scroll, and date grouping.
 */
const EmployeeNotificationBell: FC = () => {
    const { history, user } = useApp();
    const { pathname } = useLocation();

    const isHomePage = HOME_PATH_PATTERN.test(pathname);

    // Only fire the unread-count request once we have an authenticated user
    // AND we're actually going to render the bell. AppHeaderMobile may render
    // before login has finished hydrating; the home-page gate also avoids a
    // wasted request on every other route.
    const { unreadCount } = useEmployeeNotificationsUnreadCountQuery(!!user?._id && isHomePage);

    if (!isHomePage) {
        return null;
    }

    const ariaLabel = unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications';

    return (
        <IconButton
            aria-label={ariaLabel}
            onClick={() => history.push(NOTIFICATIONS_PATH)}
            className="items-center justify-center min-w-8 min-h-8 p-1 sm:p-2 text-gray-800"
        >
            <Badge count={unreadCount}>
                <NotificationsIcon className="h-[30px] w-[30px]" />
            </Badge>
        </IconButton>
    );
};

export default EmployeeNotificationBell;
